import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api';
import DataTable from '../components/DataTable';

interface AppRow {
  app_code: string;
  display_name: string;
  status: string;
  created_at: string | null;
  updated_at: string | null;
  created_by: string;
}

function format_dt(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

export default function Apps() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['apps'],
    queryFn: () => api.get<{ apps: AppRow[] }>('/api/apps'),
  });

  const [show_add, set_show_add] = useState(false);
  const [a_code, set_a_code] = useState('');
  const [a_name, set_a_name] = useState('');
  const [a_busy, set_a_busy] = useState(false);
  const [a_error, set_a_error] = useState<string | null>(null);

  async function handle_add(e: FormEvent) {
    e.preventDefault();
    set_a_busy(true);
    set_a_error(null);
    try {
      await api.post('/api/apps', {
        app_code: a_code.trim(),
        display_name: a_name.trim(),
      });
      qc.invalidateQueries({ queryKey: ['apps'] });
      set_a_code('');
      set_a_name('');
      set_show_add(false);
    } catch (err) {
      set_a_error(err instanceof ApiError ? err.reason : String(err));
    } finally {
      set_a_busy(false);
    }
  }

  async function handle_toggle_status(app: AppRow) {
    const next = app.status === 'active' ? 'deactivated' : 'active';
    if (
      next === 'deactivated' &&
      !confirm(`Deactivate ${app.display_name}? (informational only — does not block consumer reads)`)
    ) {
      return;
    }
    try {
      await api.patch(`/api/apps/${app.app_code}`, { status: next });
      qc.invalidateQueries({ queryKey: ['apps'] });
    } catch (err) {
      alert(`Failed: ${err instanceof ApiError ? err.reason : String(err)}`);
    }
  }

  return (
    <div className="container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ marginBottom: 0 }}>Apps</h1>
          <p className="muted" style={{ marginTop: 4, marginBottom: 24 }}>
            Platform apps and their ops config. Each app's per-user data is viewed via the
            user detail page; the config below is app-wide.
          </p>
        </div>
        <button onClick={() => set_show_add((v) => !v)}>
          {show_add ? 'Cancel' : 'Add app'}
        </button>
      </div>

      {show_add && (
        <div className="card" style={{ marginBottom: 24, maxWidth: 540 }}>
          <h2 style={{ marginTop: 0 }}>Add app</h2>
          <p className="muted" style={{ fontSize: 13, marginBottom: 16 }}>
            <code>app_code</code> must be lowercase letters / digits / underscores, starting
            with a letter, 2–32 chars. It is immutable after creation.
          </p>
          <form onSubmit={handle_add} style={{ display: 'grid', gap: 12 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>app_code</span>
              <input
                value={a_code}
                onChange={(e) => set_a_code(e.target.value)}
                required
                pattern="^[a-z][a-z0-9_]{1,31}$"
                placeholder="captomi_office"
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>Display name</span>
              <input
                value={a_name}
                onChange={(e) => set_a_name(e.target.value)}
                required
                maxLength={80}
                placeholder="Captomi Office"
              />
            </label>
            {a_error && <div className="error">{a_error}</div>}
            <div>
              <button type="submit" disabled={a_busy}>
                {a_busy ? 'Adding...' : 'Add'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {isLoading && <p style={{ padding: 24, margin: 0 }}>Loading...</p>}
        {error && (
          <p className="error" style={{ margin: 24 }}>
            Failed to load: {error instanceof ApiError ? error.reason : String(error)}
          </p>
        )}
        {data && (
          <DataTable
            rows={data.apps}
            columns={[
              {
                header: 'App',
                cell: (a) => (
                  <Link to={`/apps/${a.app_code}`}>
                    <strong style={a.status === 'deactivated' ? { color: 'var(--muted)' } : undefined}>
                      {a.display_name}
                    </strong>
                  </Link>
                ),
              },
              { header: 'app_code', cell: (a) => <code style={{ fontSize: 12 }}>{a.app_code}</code> },
              {
                header: 'Status',
                cell: (a) =>
                  a.status === 'active' ? (
                    <span style={{ color: 'var(--green-700)' }}>{a.status}</span>
                  ) : (
                    <span className="muted">{a.status}</span>
                  ),
              },
              { header: 'Created by', cell: (a) => a.created_by },
              { header: 'Updated', cell: (a) => format_dt(a.updated_at) },
              {
                header: '',
                cell: (a) => (
                  <button
                    className="secondary"
                    style={{ padding: '4px 10px', fontSize: 12 }}
                    onClick={() => handle_toggle_status(a)}
                  >
                    {a.status === 'active' ? 'Deactivate' : 'Activate'}
                  </button>
                ),
              },
            ]}
            empty_message="No apps registered. Click 'Add app' to register the first one."
          />
        )}
      </div>
    </div>
  );
}
